import { saveAs } from 'file-saver';
import { downloadXlsx } from './xlsxExport';

const withProvenance = (records, includeProvenance, sourceLabel) => {
    if (!includeProvenance) return records;
    const exportedAt = new Date().toISOString();
    return records.map(r => ({ ...r, _sourceDataset: sourceLabel, _exportedAt: exportedAt }));
};

/** The table shows '-' for "not reported"; files get an empty cell so numeric columns stay numeric. */
const cellValue = (val) => (val === undefined || val === null || val === '-' ? '' : val);

const csvEscape = (val) => {
    const str = String(cellValue(val));
    if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
    return str;
};

export const buildExportRows = ({ records, headers, columnsScope, includeProvenance, sourceLabel }) => {
    const cols = columnsScope === 'visible' ? headers : headers; // headers already reflect the requested scope from caller
    const rows = records.map(r => {
        const row = {};
        cols.forEach(h => { row[h] = cellValue(r[h]); });
        return row;
    });
    return withProvenance(rows, includeProvenance, sourceLabel);
};

export const exportToCSV = ({ rows, headers, filename, metadata, includeMetadata }) => {
    const cols = includeProvenanceCols(rows, headers);
    const lines = [];
    if (includeMetadata && metadata) {
        Object.entries(metadata).forEach(([k, v]) => lines.push(`# ${k}: ${v}`));
        lines.push('');
    }
    lines.push(cols.map(csvEscape).join(','));
    rows.forEach(row => lines.push(cols.map(c => csvEscape(row[c])).join(',')));
    // Byte-order mark: Excel then opens µ, ° and accents correctly
    const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, filename);
};

export const exportToJSON = ({ rows, filename, metadata, includeMetadata }) => {
    const payload = includeMetadata ? { metadata, records: rows } : rows;
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    saveAs(blob, filename);
};

// Reuses this repo's dependency-free .xlsx writer (single-sheet). Metadata/provenance
// for Excel exports travels as extra columns (via includeProvenanceCols) rather than a
// second sheet, since downloadXlsx only writes one worksheet.
export const exportToExcel = ({ rows, headers, filename }) => {
    const cols = includeProvenanceCols(rows, headers);
    const sheetData = rows.map(row => {
        const o = {};
        cols.forEach(c => { o[c] = cellValue(row[c]); });
        return o;
    });
    downloadXlsx(filename, sheetData, cols);
};

// If provenance columns are present in the rows but missing from `headers`, include them at the end.
function includeProvenanceCols(rows, headers) {
    const extra = ['_sourceDataset', '_exportedAt'].filter(k => rows.length > 0 && Object.prototype.hasOwnProperty.call(rows[0], k) && !headers.includes(k));
    return [...headers, ...extra];
}
